import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { adaptApiError, DISABLE_ERROR_INTERCEPTOR } from '@hglobal/common';
import { Observable, switchMap } from 'rxjs';

import { Account, GeoLocationForApi } from '@pp/core/entities';
import { RecaptchaService } from '@pp/core/services';
import { environment } from '@pp/environment';

import {
  AuthorizationReq,
  AuthorizationRes,
  ChangeEmailPhoneReq,
  PasswordRecoveryReq,
  SetDataReq,
  SetPhoneReq,
  SetTfaRes,
  SuccessRes,
  UserAcceptInviteReq,
  VerifyEmailPhoneReq,
} from '../model';
import { AuthApi } from './auth-api.interface';
import { InviteTokenStatus } from '../auth.entities';

const API = environment.apiHost;

@Injectable()
export class AuthApiService implements AuthApi {
  readonly #http = inject(HttpClient);
  readonly #recaptchaService = inject(RecaptchaService);

  signUp(data: UserAcceptInviteReq) {
    return this.#recaptchaService.execute().pipe(
      switchMap((token: string) => {
        return this.#http
          .post<AuthorizationRes>(
            `${API}invites/${data?.token}/`,
            { ...data, token },
            {
              context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
            },
          )
          .pipe(adaptApiError());
      }),
    );
  }

  checkInviteToken(token: string): Observable<InviteTokenStatus> {
    return this.#http
      .get<InviteTokenStatus>(`${API}invites/${token}/status/`, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  whoAmI() {
    return this.#http
      .get<Account>(`${API}accounts/whoami`, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  signIn(data: Pick<AuthorizationReq, 'login' | 'password' | 'code'>) {
    return this.#recaptchaService.execute().pipe(
      switchMap((token: string) => {
        return this.#http
          .post<AuthorizationRes>(
            `${API}auth/authorization`,
            { ...data, token },
            {
              context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
            },
          )
          .pipe(adaptApiError());
      }),
    );
  }

  setPhone(phone: SetPhoneReq) {
    return this.#http
      .post<SuccessRes>(`${API}auth/set/phone`, phone, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  changePhone(req: SetPhoneReq) {
    return this.#http
      .post<SuccessRes>(`${API}auth/reset/phone`, req, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  setData(data: SetDataReq) {
    return this.#http
      .post<SuccessRes>(`${API}auth/set/data`, data, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  verifyEmail(code: string) {
    return this.#http
      .post<SuccessRes>(
        `${API}auth/verify/email`,
        { code },
        {
          context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
        },
      )
      .pipe(adaptApiError());
  }

  resendEmailVerificationCode() {
    return this.#http
      .post<SuccessRes>(
        `${API}auth/resend/email`,
        {},
        {
          context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
        },
      )
      .pipe(adaptApiError());
  }

  verifyPhone(code: string) {
    return this.#http
      .post<SuccessRes>(
        `${API}auth/verify/phone`,
        { code },
        {
          context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
        },
      )
      .pipe(adaptApiError());
  }

  setTfa() {
    return this.#http
      .post<SetTfaRes>(`${API}accounts/2fa/set`, null, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  resendPhone() {
    return this.#http
      .post<SuccessRes>(`${API}auth/resend/phone`, null, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  recoveryEmail(email: string) {
    return this.#recaptchaService.execute().pipe(
      switchMap((token: string) => {
        return this.#http
          .post<SuccessRes>(
            `${API}auth/recovery/email`,
            { email, token },
            {
              context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
            },
          )
          .pipe(adaptApiError());
      }),
    );
  }

  recoveryPassword(payload: PasswordRecoveryReq) {
    return this.#http
      .post<SuccessRes>(`${API}auth/recovery/verify`, payload, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  activateTfa(code: string) {
    return this.#http
      .post<SuccessRes>(
        `${API}accounts/2fa/activate`,
        { code },
        {
          context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
        },
      )
      .pipe(adaptApiError());
  }

  changeEmailPhone(req: ChangeEmailPhoneReq) {
    return this.#http
      .post<SuccessRes>(`${API}auth/set/emailphone`, req, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  verifyEmailPhone(req: VerifyEmailPhoneReq) {
    return this.#http
      .post<SuccessRes>(`${API}auth/verify/emailphone`, req, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }

  setAddress(address: GeoLocationForApi) {
    return this.#http
      .post<SuccessRes>(`${API}auth/set/address`, address, {
        context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true),
      })
      .pipe(adaptApiError());
  }
}
